import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { formatDisplayDate } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { History, Loader2 } from 'lucide-react';

export default function LifecycleModal({ open, onOpenChange, type, identifier, title }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchLifecycle = useCallback(async () => {
    if (!identifier) return;
    setLoading(true);
    try {
      const endpoint = type === 'asset'
        ? `/assignments/lifecycle/${identifier}`
        : `/sim-connections/lifecycle/${identifier}`;
      const response = await api.get(endpoint);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch lifecycle');
      setData(null);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [identifier, type, onOpenChange]);

  useEffect(() => {
    if (open && identifier != null && identifier !== '') {
      fetchLifecycle();
    } else {
      setData(null);
    }
  }, [open, identifier, type, fetchLifecycle]);

  const lifecycle = data?.lifecycle || [];
  const displayTitle = title || (type === 'asset' ? `${data?.asset_name || identifier}` : data?.mobile_number || identifier);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto mx-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <History className="h-5 w-5" />
            {type === 'asset' ? 'Asset' : 'SIM'} Lifecycle
            {displayTitle && (
              <span className="font-normal text-slate-600">- {displayTitle}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : lifecycle.length === 0 ? (
          <div className="py-8 text-center text-slate-500">
            <p className="text-sm mt-2">
              {data?.message || 'No assignment history found.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {data?.current_assignment && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-sky-800 uppercase tracking-wider mb-1">Current Assignment</p>
                <p className="text-sm font-medium">
                  {data.current_assignment.employee_name} ({data.current_assignment.employee_id})
                </p>
                <p className="text-xs text-slate-600">
                  Assigned: {formatDisplayDate(data.current_assignment.assigned_date)}
                </p>
              </div>
            )}

            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Assignment History ({lifecycle.length} total)
              </p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Employee</th>
                      {type === 'sim' && (
                        <th className="px-3 py-2 text-left font-medium text-slate-600">Asset</th>
                      )}
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Assigned</th>
                      {type === 'asset' && (
                        <>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Assign Cond.</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600">Return Cond.</th>
                        </>
                      )}
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Returned</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Duration</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {lifecycle.map((item, index) => (
                      <tr
                        key={item.assignment_id}
                        className={item.status === 'active' ? 'bg-sky-50/50' : ''}
                      >
                        <td className="px-3 py-2 text-slate-600">{lifecycle.length - index}</td>
                        <td className="px-3 py-2">
                          <div>
                            <div className="font-medium">{item.employee_name}</div>
                            <div className="text-xs text-slate-500">{item.employee_id}</div>
                          </div>
                        </td>
                        {type === 'sim' && (
                          <td className="px-3 py-2">
                            {item.asset_name ? (
                              <div>
                                <div className="font-medium">{item.asset_name}</div>
                                {item.asset_id && (
                                  <div className="text-xs text-slate-500">{item.asset_id}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2 whitespace-nowrap">
                          {formatDisplayDate(item.assigned_date)}
                        </td>
                        {type === 'asset' && (
                          <>
                            <td className="px-3 py-2">
                              {item.assign_condition || '-'}
                            </td>
                            <td className="px-3 py-2">
                              {item.asset_return_condition || '-'}
                            </td>
                          </>
                        )}
                        <td className="px-3 py-2 whitespace-nowrap">
                          {item.return_date ? formatDisplayDate(item.return_date) : '-'}
                        </td>
                        <td className="px-3 py-2">
                          {item.duration_days != null ? `${item.duration_days} days` : '-'}
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            variant={item.status === 'active' ? 'default' : 'secondary'}
                            className={item.status === 'active' ? 'bg-green-600' : ''}
                          >
                            {item.status === 'active' ? 'Active' : 'Returned'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
